// @flow
import React, { Component } from "react"
import { connect, type Connector } from "react-redux"
import type { Dispatch } from "redux"
import { reduxForm } from "redux-form"

import { openChat } from "../../tools/ininChat"
import NewTextField from "common/components/NewTextField/NewTextField"
import NewTextAreaField from "common/components/NewTextAreaField/NewTextAreaField"
import Button from "common/components/Button/Button"
import Translate from "../../components/Translate"
import Alert from "common/components/Alert/Alert"
import { nameValidator, reasonValidator } from "common/utils/FieldValidatorComposer"
import cuckoo from "common/services/cuckoo/cuckoo"
import translate from "../../tools/translate"
import * as normalizers from "common/services/utils/normalizers"
import { isPagesPlatformPageSelector } from "../../../../src/content/scenes/Pages/services/pagesSelectors"
import type { LanguageInfo } from "common/records/LanguageInfo"
import { getLanguageInfo } from "common/services/staticData"
import { chatModalTextSelector } from "common/services/remoteConfig"
import { openHelp } from "common/modules/SmartFAQ/services/SmartFAQDuck"
import { bidSelector } from "mmb/scenes/AccountBooking/components/Booking/services/selector"
import type { BookingId } from "mmb/types/primitiveTypes"

const thankyouText = <Translate tKey="common.thanks_for_contacting" className="ChatModal-text" />
const isHelpCenter = (page: ?string) => (page ? page.includes("helpCenter") : false)
type State = {
  view: "name" | "reason" | "bid",
  name?: string,
  bid?: number,
  reason?: string,
}

type ParentProps = {
  onClose: () => void,
  isHelpCenter: boolean,
}

type ReduxFormProps = {
  fields: { name: any, bid: any, reason: any }, // redux form object
  form: string,
  validate: any => { name: ?string, reason: ?string },
}

type Props = {
  ...ParentProps,
  ...ReduxFormProps,
  dispatch: Dispatch<any>,
  page: string,
  isPagesPlatformPage: boolean,
  chatModalText: string,
  langInfo: LanguageInfo,
  isPartner: boolean,
  isMMBPage: boolean,
  bookingId: BookingId,
  openHelpCenter: () => void,
}

class ChatModal extends Component<Props, State> {
  state = { view: "name" }

  handleSubmit = () => {
    const {
      onClose,
      // $FlowFixMe - name, bid, reason is missing in property fields of unknown type
      fields: { name, bid, reason },
      bookingId,
      isMMBPage,
    } = this.props

    openChat({
      name: name.value,
      bid: bid.value || bookingId,
      reason: reason.value,
      queueName: isMMBPage ? "Chat_Partner_EN" : "Chat_EN",
    })
    // $FlowFixMe - unknown type/undefined is not a function
    onClose() // close modal
  }

  handleOpenHelpCentre = () => {
    const { onClose, openHelpCenter } = this.props

    openHelpCenter()
    cuckoo.infinario("helpCenterAction", { action: "helpOpened", specify: "chatModal" })
    // $FlowFixMe - unknown type/undefined is not a function
    onClose() // close modal
  }

  changeView(view) {
    this.setState({ view })
  }

  renderView() {
    const { isMMBPage, langInfo, fields } = this.props
    switch (this.state.view) {
      case "name":
        return (
          <div>
            {thankyouText}
            <div className="ChatModal-headline">
              <Translate tKey="common.enter_fullname" />
            </div>
            <NewTextField
              name="name"
              dataTest="ChatModal-name"
              placeholder={`${translate("common.for_example")} ${langInfo.firstNamePlaceholder}`}
              value={this.state.name || null}
              // $FlowFixMe - name is missing in property fields of unknown type
              {...fields.name}
            />
            <Button
              fullwidth
              dataTest="ChatModal-next"
              // $FlowFixMe - name is missing in property fields of unknown type
              disabled={!fields.name.valid}
              onClick={() => this.changeView(isMMBPage ? "reason" : "bid")}
            >
              <Translate tKey="common.continue" />
            </Button>
          </div>
        )

      case "bid":
        return (
          <div>
            <i
              className="ChatModal-back spIcon ic_arrow_forward"
              data-test="ChatModal-back-icon"
              onClick={() => this.changeView("name")}
            />
            {thankyouText}
            <div className="ChatModal-headline">
              <Translate tKey="common.enter_bid" />
            </div>
            <NewTextField
              name="name"
              dataTest="ChatModal-bid"
              placeholder="Check your email or SMS"
              value={this.state.bid}
              normalize={normalizers.numberNormalizer}
              // $FlowFixMe - bid is missing in property fields of unknown type
              {...fields.bid}
            />
            <Button
              fullwidth
              dataTest="ChatModal-next"
              onClick={() => {
                cuckoo.infinario("helpCenterAction", {
                  action: "buttonClick",
                  specify: "chatModal",
                  status: "bookingNumber",
                })
                this.changeView("reason")
              }}
              // $FlowFixMe - bid is missing in property fields of unknown type
              disabled={fields.bid.value.length <= 4}
            >
              <Translate tKey="common.continue" />
            </Button>
            <span
              className="ChatModal-skip spTypoLink"
              onClick={() => {
                cuckoo.infinario("helpCenterAction", {
                  action: "buttonClick",
                  specify: "chatModal",
                  status: "noBookingNumber",
                })
                this.changeView("reason")
              }}
            >
              <Translate tKey="common.do_not_have_bid" />
            </span>
          </div>
        )

      case "reason":
        return (
          <div>
            <i
              className="ChatModal-back spIcon ic_arrow_forward"
              data-test="ChatModal-back-icon"
              onClick={() => this.changeView(isMMBPage ? "name" : "bid")}
            />
            {thankyouText}
            <div className="ChatModal-headline">
              <Translate tKey="common.enter_reason" />
            </div>
            <NewTextAreaField
              name="reason"
              dataTest="ChatModal-reason"
              type="text"
              placeholder={`${translate("refund.reason_title")}...`}
              value={this.state.reason || null}
              // $FlowFixMe - reason is missing in property fields of unknown type
              {...fields.reason}
            />
            <Button
              fullwidth
              dataTest="ChatModal-next"
              onClick={this.handleSubmit}
              // $FlowFixMe - reason is missing in property fields of unknown type
              disabled={!fields.reason.valid}
            >
              <Translate tKey="common.continue" />
            </Button>
          </div>
        )
      default:
        return null
    }
  }

  render() {
    const { isPagesPlatformPage, chatModalText, langInfo, page } = this.props
    const shouldRenderLanguageWarning = langInfo.translations !== "en"

    return (
      <div className="ChatModal" data-test="ChatModal">
        {Boolean(chatModalText) && (
          <Alert type="error" transparent bordered closable={false}>
            <Translate tKey={chatModalText} />
          </Alert>
        )}
        {shouldRenderLanguageWarning && (
          <Alert type="warning" light bordered closable={false}>
            <Translate tKey="common.chat_only_english" />
          </Alert>
        )}
        {this.renderView()}
        <div className="ChatModal-service">
          <Translate tKey="common.best_google_chrome" />
        </div>
        {!isHelpCenter(page) &&
        !isPagesPlatformPage && ( // don't render link to helpCenter on helpCenter and content pages
            <span
              onClick={this.handleOpenHelpCentre}
              className="ChatModal-help spTypoLink flexVerticalCenter"
              data-test="ChatModal-help"
            >
              <i className="ChatModal-helpIcon spIcon ic_help icon" />
              <Translate tKey="help_center.open_original" />
            </span>
          )}
      </div>
    )
  }
}

const form = reduxForm({
  form: "ininChat",
  fields: ["name", "bid", "reason"],
  validate: ({ name, reason }) => ({
    name: nameValidator.validate(name),
    reason: reasonValidator.validate(reason),
  }),
})

const connector: Connector<{ ...ReduxFormProps, ...ParentProps }, Props> = connect(
  state => ({
    page: state.page.get("name"),
    langInfo: getLanguageInfo(state),
    isPagesPlatformPage: isPagesPlatformPageSelector(state),
    chatModalText: chatModalTextSelector(state),
    isPartner: Boolean(state.user.user && state.user.user.affiliateId),
    isMMBPage: ["manage", "account"].includes(state.page.get("namesPath")),
    bookingId: bidSelector(state),
  }),
  dispatch => ({
    openHelpCenter: () => dispatch(openHelp()),
  }),
)

export default connector(form(ChatModal))
